import React, { useState, useEffect } from 'react';
import { Target, Lightbulb, Users, Target as Pragmatism, Rocket, Star, Sparkles } from 'lucide-react';
import { useIntl } from 'react-intl';
import SectionGuard from './common/SectionGuard';
import SkeletonImage from './common/SkeletonImage';

const ValueTooltip = ({ value, description, children }) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div
      className="relative"
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      {children}
      {isVisible && description ? (
        <div
          className="absolute bottom-full left-0 mb-3 z-[100]"
          style={{ animation: 'tooltip-fade-in 0.2s ease-out' }}
        >
          <div className="relative bg-[#0a1628] text-white px-5 py-4 max-w-[280px]">
            <p className="text-sm font-semibold text-white mb-1.5">{value}</p>
            <p className="text-xs text-slate-300 leading-relaxed">{description}</p>
            <div className="absolute top-0 left-0 w-0.5 h-full bg-[#16A34A]" />
          </div>
        </div>
      ) : null}
    </div>
  );
};

const AboutSection = ({ aboutData }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('history');
  const intl = useIntl();

  const {
    title,
    description,
    history,
    vision,
    mission,
    values,
    image = {}
  } = aboutData || {};

  const imageUrl = image?.url
    ? `${image.url}`
    : image?.data?.attributes?.url
      ? `${image.data.attributes.url}`
      : '';

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const tabs = [
    { id: 'history', label: intl.formatMessage({ id: 'about.history' }), content: history },
    { id: 'mission', label: intl.formatMessage({ id: 'about.mission' }), content: mission },
    { id: 'vision', label: intl.formatMessage({ id: 'about.vision' }), content: vision }
  ];

  const valueIcons = {
    Innovation: <Lightbulb className="w-4 h-4" />,
    "Travail d'équipe": <Users className="w-4 h-4" />,
    Pragmatisme: <Pragmatism className="w-4 h-4" />,
    Ambition: <Rocket className="w-4 h-4" />,
    Enthousiasme: <Star className="w-4 h-4" />,
    Simplicité: <Sparkles className="w-4 h-4" />
  };

  const hasContent = Boolean(
    title || description || history || vision || mission || (values && values.length) || imageUrl
  );

  return (
    <SectionGuard hasContent={hasContent} emptyMessageId="empty.section">
      <section className="relative py-20 lg:py-28 bg-white overflow-visible">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
          <div
            className={`grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            {/* Contenu éditorial */}
            <div className="lg:col-span-5 flex flex-col gap-10 lg:sticky lg:top-28">
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <span className="w-8 h-px bg-[#16A34A]" />
                  <span className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#16A34A]">
                    Africa Systems
                  </span>
                </div>
                <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold text-[#0a1628] tracking-tight leading-[1.1]">
                  {title}
                </h2>
                <p className="mt-6 text-slate-600 text-base lg:text-lg leading-relaxed max-w-md">
                  {description}
                </p>
              </div>

              <div>
                <div className="flex flex-wrap gap-x-6 gap-y-2 border-b border-slate-200">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`pb-3 relative text-[11px] font-semibold uppercase tracking-[0.16em] transition-colors duration-300 ${
                        activeTab === tab.id
                          ? 'text-[#16A34A]'
                          : 'text-slate-400 hover:text-slate-600'
                      }`}
                    >
                      {tab.label}
                      {activeTab === tab.id && (
                        <span className="absolute bottom-0 left-0 w-full h-0.5 bg-[#16A34A]" />
                      )}
                    </button>
                  ))}
                </div>
                <p className="mt-6 text-slate-700 text-base leading-relaxed border-l-2 border-[#16A34A] pl-5">
                  {tabs.find((tab) => tab.id === activeTab)?.content}
                </p>
              </div>
            </div>

            {/* Image dominante */}
            <div className="lg:col-span-7">
              <div className="relative">
                <div className="absolute -top-3 -left-3 w-16 h-16 border-t-2 border-l-2 border-[#16A34A] pointer-events-none hidden lg:block" />
                <div className="overflow-hidden bg-slate-100">
                  {imageUrl ? (
                    <SkeletonImage
                      src={imageUrl}
                      alt={intl.formatMessage({ id: 'about.imageAlt' })}
                      className="w-full min-h-[320px] lg:min-h-[480px]"
                      imgClassName="w-full h-auto min-h-[320px] lg:min-h-[480px] object-cover"
                    />
                  ) : null}
                </div>
                <div className="mt-4 flex items-center justify-between text-[11px] tracking-[0.18em] uppercase text-slate-400">
                  <span>Est. 2017</span>
                  <span>{intl.formatMessage({ id: 'about.countriesBadge' })}</span>
                </div>
              </div>

              {(Array.isArray(values) ? values : []).length > 0 && (
                <div className="mt-12 lg:mt-16">
                  <h3 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400 mb-5">
                    Nos Valeurs
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3">
                    {(Array.isArray(values) ? values : []).map((valueObj, index) => {
                      const valueName = typeof valueObj === 'string' ? valueObj : valueObj.name;
                      const valueDescription =
                        typeof valueObj === 'string' ? '' : valueObj.description || '';
                      return (
                        <ValueTooltip
                          key={typeof valueObj === 'string' ? index : valueObj.id}
                          value={valueName}
                          description={valueDescription}
                        >
                          <div className="flex items-center gap-2.5 py-1.5 group cursor-default">
                            <span className="text-[#16A34A] opacity-70 group-hover:opacity-100 transition-opacity">
                              {valueIcons[valueName] || <Target className="w-4 h-4" />}
                            </span>
                            <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
                              {valueName}
                            </span>
                          </div>
                        </ValueTooltip>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </SectionGuard>
  );
};

export default AboutSection;
